// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useSyncExternalStore } from "react"

// Number of consecutive failures to flip isBackendDown — never hardcoded at call
// sites. Referenced by M6 MaintenanceDetector and M7 MaintenanceView.
export const MAINTENANCE_THRESHOLD = 3

export type MaintenanceState = {
  isBackendDown: boolean
  consecutiveFailures: number
}

type Listener = () => void

let state: MaintenanceState = {
  isBackendDown: false,
  consecutiveFailures: 0,
}

const listeners = new Set<Listener>()

function notify(): void {
  listeners.forEach((l) => l())
}

// Required by `useSyncExternalStore`.
function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// Required by `useSyncExternalStore`.
function getSnapshot(): MaintenanceState {
  return state
}

function setBackendDown(value: boolean): void {
  state = { ...state, isBackendDown: value }
  notify()
}

function incrementFailures(): void {
  const next = state.consecutiveFailures + 1
  const isDown = next >= MAINTENANCE_THRESHOLD
  state = { consecutiveFailures: next, isBackendDown: isDown }
  notify()
}

// Called by the interceptor success handler (any 2xx-3xx clears immediately)
// and by test beforeEach hooks for isolation.
function resetFailures(): void {
  state = { isBackendDown: false, consecutiveFailures: 0 }
  notify()
}

export const maintenanceStore = {
  subscribe,
  getSnapshot,
  setBackendDown,
  incrementFailures,
  resetFailures,
  // Alias for resetFailures — test-facing name for clarity.
  reset: resetFailures,
}

export function useMaintenanceStore(): MaintenanceState {
  return useSyncExternalStore(subscribe, getSnapshot)
}
